import {type ReactNode, createContext, useContext, useMemo} from 'react';
import {buildLocalizedMicrositeHref} from '../../routes/pathHelpers';
import {useMicrositeRoute} from '../../runtime/MicrositeRouteContext';
import {type LanguageId, validateLanguageOrder} from '../../translations/utils/languageManifest';
import type {LanguageSelectorTranslation, LanguageSelectorTranslationContent} from './LanguageSelectorTranslation';
import type {MicrositeRouteDefinition} from '../../routes/types';

type LanguageSelectorTranslationsContextValue = {
  readonly locale: LanguageSelectorTranslationContent;
  readonly english: LanguageSelectorTranslationContent;
  readonly languageOrder: readonly LanguageId[];
};

const LanguageSelectorTranslationContext = createContext<LanguageSelectorTranslationsContextValue | null>(null);

type LanguageSelectorTranslationProviderProps = {
  readonly value: LanguageSelectorTranslationsContextValue;
  readonly children: ReactNode;
};

export const LanguageSelectorTranslationProvider = ({value, children}: LanguageSelectorTranslationProviderProps) => {
  const ensuredLanguageOrder = validateLanguageOrder(value.languageOrder);
  return <LanguageSelectorTranslationContext.Provider value={{locale: value.locale, english: value.english, languageOrder: ensuredLanguageOrder}}>{children}</LanguageSelectorTranslationContext.Provider>;
};

const resolveRouteLanguageOrder = (route: MicrositeRouteDefinition, languageOrder: readonly LanguageId[]): LanguageId[] => {
  const definedLocales = Object.keys(route.paths);

  if (definedLocales.length === 0) {
    throw new Error(`Microsite route "${route.id}" must define at least one localized path.`);
  }

  const languageOrderLookup = new Set(languageOrder);

  const typedLocales: LanguageId[] = definedLocales.map((localeId) => {
    if (!languageOrderLookup.has(localeId as LanguageId)) {
      throw new Error(`Microsite route "${route.id}" defines unsupported locale "${localeId}".`);
    }
    return localeId as LanguageId;
  });

  const localeSet = new Set(typedLocales);
  const orderedLocales = languageOrder.filter((languageId) => localeSet.has(languageId));

  if (orderedLocales.length === 0) {
    throw new Error(`Microsite route "${route.id}" failed to resolve any localized paths.`);
  }

  if (orderedLocales.length !== typedLocales.length) {
    const missingOrderLocales = typedLocales.filter((localeId) => !orderedLocales.includes(localeId));
    if (missingOrderLocales.length > 0) {
      throw new Error(`Language order is missing locale(s) required by route "${route.id}": ${missingOrderLocales.join(`, `)}.`);
    }
  }

  return orderedLocales;
};

export const useLanguageSelectorTranslations = (): LanguageSelectorTranslation => {
  const context = useContext(LanguageSelectorTranslationContext);

  if (!context) {
    throw new Error(`useLanguageSelectorTranslations must be used within a LanguageSelectorTranslationProvider`);
  }

  const {locale, english, languageOrder} = context;
  const route = useMicrositeRoute();
  const {languageOptions: localeLanguageOptions, ...rest} = locale;
  const englishLanguageOptions = english.languageOptions;
  const orderedRouteLanguages = useMemo(() => resolveRouteLanguageOrder(route, languageOrder), [languageOrder, route]);

  const resolvedOptions = useMemo(
    () =>
      orderedRouteLanguages.map((languageId) => {
        const localizedOption = localeLanguageOptions[languageId];
        const englishOption = englishLanguageOptions[languageId];

        if (!localizedOption) {
          throw new Error(`Missing language option copy for ${languageId}`);
        }

        if (!englishOption) {
          throw new Error(`Missing en-US language option copy for ${languageId}`);
        }

        return {
          id: languageId,
          label: localizedOption.label,
          englishLabel: englishOption.label,
          countryCodes: localizedOption.countryCodes,
          href: buildLocalizedMicrositeHref(route, languageId),
        };
      }),
    [englishLanguageOptions, localeLanguageOptions, orderedRouteLanguages, route]
  );

  return {
    ...rest,
    languageOptions: resolvedOptions,
  };
};
